import React, { useEffect, useState } from 'react';
import { ShieldAlert, Send, Stethoscope, RotateCcw, UserPlus, Lock, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import { THREAD_STATUS, URGENCY, REQUEST_TYPES, CLINICIAN_ACTIONS, ACTION_LABELS } from '../lib/clinicalMessages';
import '../components/ClinicalMessages.css';

export default function ClinicalThread({ user, notify, navigate, id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [actioning, setActioning] = useState(null); // { action_type, body }
  const [assignTo, setAssignTo] = useState('');
  const [recipients, setRecipients] = useState([]);

  async function load() {
    setLoading(true);
    try { setData(await api(`/clinical-message-threads/${id}`)); }
    catch (e) { notify(e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hasCap(user, 'clinical_messages.assign')) api('/clinical-message-recipients').then(d => setRecipients(d.users || [])).catch(() => {}); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !data) return <div className="content-card generation-skeleton" role="status"><i/><i/><i/></div>;

  const { thread, messages, events } = data;
  const st = THREAD_STATUS[thread.status] || { label: thread.status, tone: 'neutral' };
  const ug = URGENCY[thread.urgency] || { label: thread.urgency, tone: 'neutral' };
  const canReply = hasCap(user, 'clinical_messages.reply');
  const canAct = hasCap(user, 'clinical_messages.clinician_action');
  const canAssign = hasCap(user, 'clinical_messages.assign');
  const canClose = hasCap(user, 'clinical_messages.close');

  async function sendReply(e) {
    e.preventDefault(); if (!reply.trim()) return; setBusy(true);
    try { await api(`/clinical-message-threads/${id}/messages`, { method: 'POST', body: { body: reply.trim() } }); setReply(''); await load(); }
    catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function sendAction(e) {
    e.preventDefault(); if (!actioning.body.trim()) { notify('Include a narrative with the action.'); return; } setBusy(true);
    try {
      await api(`/clinical-message-threads/${id}/actions`, { method: 'POST', body: { action_type: actioning.action_type, body: actioning.body.trim() } });
      const completed = actioning.action_type === 'COMPLETE_CERTIFICATION';
      notify(completed ? 'Certification request recorded. Complete the formal certification in the chart.' : 'Action recorded.');
      setActioning(null);
      await load();
      if (completed && thread.patient_id) navigate(`/patient/${thread.patient_id}`);
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function assign() {
    if (!assignTo) return; setBusy(true);
    try { await api(`/clinical-message-threads/${id}/assign`, { method: 'POST', body: { assigned_to: assignTo } }); setAssignTo(''); notify('Thread reassigned.'); await load(); }
    catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function closeThread() {
    if (!window.confirm('Close this thread? It will no longer be active.')) return; setBusy(true);
    try { await api(`/clinical-message-threads/${id}/status`, { method: 'POST', body: { status: 'CLOSED' } }); notify('Thread closed.'); await load(); }
    catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function retract(m) {
    const reason = window.prompt('Reason for retraction (shown to others):') || '';
    setBusy(true);
    try { await api(`/clinical-messages/${m.id}/retract`, { method: 'POST', body: { reason } }); notify('Message retracted.'); await load(); }
    catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return <>
    <div className="page-heading"><div><div className="eyebrow">CLINICAL THREAD · {REQUEST_TYPES[thread.request_type] || thread.request_type}</div><h1>{thread.subject || 'Clinical request'}.</h1><p>Thread # {thread.id}{thread.patient_name ? ` · re: ${thread.patient_name}` : ''}</p></div>
      <div className="heading-actions">{thread.patient_id && <button className="button ghost" onClick={() => navigate(`/patient/${thread.patient_id}`)}>Open chart</button>}</div></div>

    <div className="cm-disclaimer"><ShieldAlert size={16}/><span>Clinical messaging is not an emergency response system — use emergency protocols for urgent clinical events. A chat “Approved” does not constitute a formal physician certification or signature; use the chart’s certification workflow to sign.</span></div>

    <section className="content-card">
      <div className="cm-thread-head">
        <div className="cm-thread-badges"><span className={`cm-badge ${st.tone}`}>{st.label}</span><span className={`cm-badge ${ug.tone}`}>{ug.label}</span>{thread.request_type && <span className="cm-badge pending">{REQUEST_TYPES[thread.request_type] || thread.request_type}</span>}</div>
        <div className="cm-thread-patient">{thread.creator_name ? `Requested by ${thread.creator_name}` : ''}{thread.assignee_name ? ` · assigned to ${thread.assignee_name}` : ' · unassigned'}</div>
      </div>

      <div className="cm-timeline">
        {messages.map(m => {
          const mine = String(m.sender_id) === String(user.id);
          const retracted = m.retraction_reason != null || m.retracted_by != null;
          return <div key={m.id} className={`cm-msg ${mine ? 'me' : ''} ${m.message_type === 'action' ? 'action' : ''} ${retracted ? 'retracted' : ''}`}>
            <div className="cm-msg-head">
              <strong>{m.sender_name || 'Colleague'}</strong>
              <small>{new Date(m.created_at).toLocaleString()}</small>
              <span className="cm-msg-actions">{mine && !retracted && <button className="text-button" onClick={() => retract(m)} title="Retract"><RotateCcw size={13}/> Retract</button>}</span>
            </div>
            {m.action_type && <div className="cm-badge pending" style={{ marginBottom: 6 }}>{ACTION_LABELS[m.action_type] || m.action_type}</div>}
            {retracted ? <div className="cm-msg-body">Retracted by {m.retracted_by || 'the sender'}{m.retraction_reason ? `: ${m.retraction_reason}` : ''}</div> : <div className="cm-msg-body">{m.body}</div>}
            {m.metadata && Object.keys(m.metadata).length > 0 && <div className="cm-msg-meta-fields">{Object.entries(m.metadata).map(([k, v]) => <div key={k}><strong>{k.replace(/_/g, ' ')}:</strong> {Array.isArray(v) ? v.join(', ') : String(v)}</div>)}</div>}
          </div>;
        })}
        {!messages.length && <p className="empty-copy">No messages yet.</p>}
      </div>

      {(events || []).length > 0 && <div className="cm-events"><h4>Thread history</h4>{events.map(e => <div className="cm-event" key={e.id}>{e.actor_name ? `${e.actor_name} ` : ''}<strong>{e.event_type.replace(/_/g, ' ')}</strong>{e.old_value ? ` ${e.old_value}` : ''}{e.new_value ? ` → ${e.new_value}` : ''} · {new Date(e.created_at).toLocaleString()}</div>)}</div>}

      {canAct && !['APPROVED', 'DECLINED', 'CLOSED'].includes(thread.status) && (
        <div className="cm-action-bar">
          {CLINICIAN_ACTIONS.map(a => <button key={a} className={`button ${a === 'APPROVE' ? 'primary' : 'ghost'}`} onClick={() => setActioning({ action_type: a, body: '' })}><Stethoscope size={14}/>{ACTION_LABELS[a]}</button>)}
          {canAssign && <><UserPlus size={14}/><select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={{ padding: '7px 9px', borderRadius: 6, border: '1px solid #dce4e7' }}><option value="">Reassign…</option>{recipients.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>{assignTo && <button className="button ghost" onClick={assign} disabled={busy}>Assign</button>}</>}
          {canClose && <button className="button ghost" onClick={closeThread} disabled={busy}><Lock size={14}/>Close thread</button>}
        </div>
      )}

      {actioning && <form className="cm-compose" style={{ flexDirection: 'column', alignItems: 'stretch' }} onSubmit={sendAction}>
        <div style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 600 }}>{ACTION_LABELS[actioning.action_type]} — add a narrative (required):</div>
        <textarea rows={3} autoFocus value={actioning.body} onChange={e => setActioning(a => ({ ...a, body: e.target.value }))} placeholder={actioning.action_type === 'COMPLETE_CERTIFICATION' ? 'Document that certification is required; then complete the formal e-signature in the chart.' : 'Narrative…'}/>
        <div style={{ display: 'flex', gap: 8 }}><button className="button primary" disabled={busy}><CheckCircle2 size={14}/>{busy ? 'Saving…' : `Confirm ${ACTION_LABELS[actioning.action_type]}`}</button><button type="button" className="button ghost" onClick={() => setActioning(null)}>Cancel</button></div>
      </form>}

      {canReply && !['CLOSED'].includes(thread.status) && <form className="cm-compose" onSubmit={sendReply}><textarea aria-label="Reply" rows={2} maxLength={8000} value={reply} onChange={e => setReply(e.target.value)} placeholder="Write a reply…"/><button className="button primary" disabled={busy || !reply.trim()}><Send size={14}/></button></form>}
    </section>
  </>;
}
